/**
 * Maintenance-mode state for the portal-wide takeover / banner.
 *
 * Module-scoped intentionally — every layout copy reads the same value.
 * SSR-safe: the initial ref stays at `enabled: false` until the client
 * mounts and the first fetch runs, so SSR'd pages never accidentally
 * render the takeover (which would be wrong for the static login /
 * setup pages anyway).
 *
 * Polls every 5 minutes by default. Tab-hidden pauses + visibility-
 * change refresh, same pattern as useInboxUnread. Calls refresh()
 * manually after an admin toggles the flag from /admin/server so the
 * banner updates instantly without waiting for the next poll.
 */
export type MaintenanceState = {
  enabled: boolean
  message: string | null
  enabledAtUtc: string | null
}

const state = ref<MaintenanceState>({ enabled: false, message: null, enabledAtUtc: null })
const loading = ref(false)
let pollTimer: any = null
let visibilityListenerAttached = false

// Mirrors useInboxUnread's cadence — 5 minutes is plenty for a flag
// that only flips when an admin clicks a button. Override via
// startPolling(intervalMs) at the call site if a particular page
// wants a tighter cadence.
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000

async function fetchState() {
  if (typeof document !== 'undefined' && document.hidden) return
  loading.value = true
  try {
    const res: any = await $fetch('/api/public/maintenance', { credentials: 'include' })
    state.value = {
      enabled: !!res?.enabled,
      message: res?.message ?? null,
      enabledAtUtc: res?.enabledAtUtc ?? null
    }
  } catch {
    // Backend unreachable — leave the previous state in place rather
    // than flipping to false (which would briefly take the user OFF
    // the takeover during a hiccup, only to put them back on it
    // moments later when the next poll succeeds).
  } finally {
    loading.value = false
  }
}

function startPolling(intervalMs: number = DEFAULT_POLL_INTERVAL_MS) {
  if (typeof window === 'undefined') return
  stopPolling()
  pollTimer = setInterval(fetchState, intervalMs)
  if (!visibilityListenerAttached) {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) fetchState()
    })
    visibilityListenerAttached = true
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

export function useMaintenanceMode() {
  return {
    state: readonly(state),
    loading: readonly(loading),
    refresh: fetchState,
    startPolling,
    stopPolling
  }
}
